// src/components/Store/Slices/financialSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import BaseUrl from '../../../BaseUrl';

// Async thunks for fetching catalog data
export const fetchServiceGroups = createAsyncThunk(
  'financial/fetchServiceGroups',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.labsurl}/ServiceGroups`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch service groups');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchLabTests = createAsyncThunk(
  'financial/fetchLabTests',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.labsurl}/LabTestServices`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      console.log(response)
      if (!response.ok) {
        throw new Error('Failed to fetch lab tests');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMedicines = createAsyncThunk(
  'financial/fetchMedicines',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.pharmacyurl}/PharmacyPurchases`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch medicines');
      }
      
      const data = await response.json();
      
      const uniqueMedicines = new Map();
      data.forEach(purchase => {
        const medicineId = purchase.medicineId;
        if (!uniqueMedicines.has(medicineId)) {
          uniqueMedicines.set(medicineId, {
            id: medicineId,
            medicinePurchaseId: purchase.id,
            name: purchase.medicine?.name || 'Unknown',
            generic: purchase.medicine?.generic || '',
            dosage: purchase.medicine?.dosage || '',
            modeOfAdministration: purchase.medicine?.modeOfAdministration || '',
            manufacturer: purchase.medicine?.manufacturer || '',
            medicinePurpose: purchase.medicine?.medicinePurpose || '',
            price: purchase.storePrice || purchase.unitSellingPrice || 0,
            unitCost: purchase.unitCost || 0,
            storePrice: purchase.storePrice || 0,
            unitSellingPrice: purchase.unitSellingPrice || 0,
            numberOfUnits: purchase.numberOfUnits || 0,
            barcode: purchase.barcode || '',
            expiryDate: purchase.expiryDate,
            purchaseDate: purchase.purchaseDate
          });
        }
      });
      
      return Array.from(uniqueMedicines.values());
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOtherServices = createAsyncThunk(
  'financial/fetchOtherServices',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.labsurl}/LabTestServices`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch services');
      }
      
      const data = await response.json();
      const otherServices = data.filter(service => service.groupId === 16);
      return otherServices;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchHealthPackages = createAsyncThunk(
  'financial/fetchHealthPackages',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BaseUrl.labsurl}/Packages`, {
        method: 'GET',
        headers: {
          'accept': 'text/plain'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch health packages');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// NEW: Fetch Food Categories
export const fetchFoodCategories = createAsyncThunk(
  'financial/fetchFoodCategories',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.hmsurl}FoodCategories/FoodCategoryList`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch food categories');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// NEW: Fetch Menus (Food Items)
export const fetchMenus = createAsyncThunk(
  'financial/fetchMenus',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.hmsurl}Menus`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch menus');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createOtherService = createAsyncThunk(
  'financial/createOtherService',
  async (serviceData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.labsurl}/LabTestServices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          serviceName: serviceData.serviceName,
          description: serviceData.description || '',
          status: true,
          price: serviceData.price,
          groupId: 16
        })
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Failed to create service');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// NEW: Fetch Payment Modes
export const fetchPaymentModes = createAsyncThunk(
  'financial/fetchPaymentModes',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${BaseUrl.invoiceurl}PaymentTypeMasters`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch payment modes');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// NEW: Fetch Patient Policies by MRN
export const fetchPatientPoliciesByMRN = createAsyncThunk(
  'financial/fetchPatientPoliciesByMRN',
  async (mrn, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${BaseUrl.templatesurl}PatientPolicies/PatientPolicyByMRN/${mrn}`,
        {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch patient policies');
      }
      
      const data = await response.json();
      
      // Handle both single policy and array of policies
      let policies = [];
      if (Array.isArray(data)) {
        policies = data;
      } else if (data && typeof data === 'object') {
        policies = [data];
      }
      
      // Filter only active policies
      const activePolicies = policies.filter(policy => 
        policy.status?.toLowerCase() === 'active'
      );
      
      return activePolicies;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const financialSlice = createSlice({
  name: 'financial',
  initialState: {
    serviceGroups: [],
    labTests: [],
    medicines: [],
    otherServices: [],
    healthPackages: [],
    foodCategories: [],
    menus: [],
    paymentModes: [],
    patientPolicies: [],
    selectedPaymentMode: '',
    selectedInsurance: null,
    loading: false,
    creatingService: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addNewServiceLocally: (state, action) => {
      state.otherServices.push(action.payload);
    },
    setSelectedPaymentMode: (state, action) => {
      state.selectedPaymentMode = action.payload;
    },
    setSelectedInsurance: (state, action) => {
      state.selectedInsurance = action.payload;
    },
    setPatientPolicies: (state, action) => {
      state.patientPolicies = action.payload;
    },
    clearPaymentSelection: (state) => {
      state.selectedPaymentMode = '';
      state.selectedInsurance = null;
      state.patientPolicies = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServiceGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.serviceGroups = action.payload;
      })
      .addCase(fetchServiceGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchLabTests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLabTests.fulfilled, (state, action) => {
        state.loading = false;
        state.labTests = action.payload;
      })
      .addCase(fetchLabTests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMedicines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMedicines.fulfilled, (state, action) => {
        state.loading = false;
        state.medicines = action.payload;
      })
      .addCase(fetchMedicines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOtherServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOtherServices.fulfilled, (state, action) => {
        state.loading = false;
        state.otherServices = action.payload;
      })
      .addCase(fetchOtherServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchHealthPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHealthPackages.fulfilled, (state, action) => {
        state.loading = false;
        state.healthPackages = action.payload;
      })
      .addCase(fetchHealthPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchFoodCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFoodCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.foodCategories = action.payload;
      })
      .addCase(fetchFoodCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMenus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenus.fulfilled, (state, action) => {
        state.loading = false;
        state.menus = action.payload;
      })
      .addCase(fetchMenus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createOtherService.pending, (state) => {
        state.creatingService = true;
        state.error = null;
      })
      .addCase(createOtherService.fulfilled, (state, action) => {
        state.creatingService = false;
        state.otherServices.push(action.payload);
      })
      .addCase(createOtherService.rejected, (state, action) => {
        state.creatingService = false;
        state.error = action.payload;
      })
      // Payment Modes
      .addCase(fetchPaymentModes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentModes.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentModes = action.payload;
      })
      .addCase(fetchPaymentModes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Patient Policies
      .addCase(fetchPatientPoliciesByMRN.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientPoliciesByMRN.fulfilled, (state, action) => {
        state.loading = false;
        state.patientPolicies = action.payload;
      })
      .addCase(fetchPatientPoliciesByMRN.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Selectors
export const selectServiceGroups = (state) => state.financial.serviceGroups;
export const selectLabTests = (state) => state.financial.labTests;
export const selectMedicines = (state) => state.financial.medicines;
export const selectOtherServices = (state) => state.financial.otherServices;
export const selectHealthPackages = (state) => state.financial.healthPackages;
export const selectFoodCategories = (state) => state.financial.foodCategories;
export const selectMenus = (state) => state.financial.menus;
export const selectPaymentModes = (state) => state.financial.paymentModes;
export const selectPatientPolicies = (state) => state.financial.patientPolicies;
export const selectSelectedPaymentMode = (state) => state.financial.selectedPaymentMode;
export const selectSelectedInsurance = (state) => state.financial.selectedInsurance;
export const selectLoading = (state) => state.financial.loading;
export const selectCreatingService = (state) => state.financial.creatingService;
export const selectError = (state) => state.financial.error;

export const { 
  clearError, 
  addNewServiceLocally, 
  setSelectedPaymentMode, 
  setSelectedInsurance, 
  setPatientPolicies,
  clearPaymentSelection 
} = financialSlice.actions;

export default financialSlice.reducer;